import { useId, type ReactNode } from 'react';
import { Link } from 'react-router';

import { artworkUrl } from '@shared/assets/artwork';
import { formatNumber, toArabicDigits } from '@shared/format';
import { Alert } from '@shared/ui/Alert';
import { VocabularyPicker } from '@shared/ui/picker';

import { editorCopy } from './copy';
import { PostStateBadge } from './PostStateBadge';
import type { ArtworkPolicy } from './queries';

/** Which of the two artwork controls an upload — and its refusal — belongs to. */
export type ArtworkSlot = 'thumbnail' | 'hero';

/** A refusal, carried with the control it answers so it renders beside it. */
export interface ArtworkFailure {
  slot: ArtworkSlot;
  message: string;
}
import type { EditorialState, PostTag, RelatedTitle } from './types';

/**
 * The published contract, in the order both hints take it.
 *
 * A tuple rather than four arguments spelled out twice, because the two hints
 * state the same four facts and a mismatched order between them is a class of
 * bug no reviewer catches: the sentence still reads.
 *
 * **Zeroes while the read is in flight, never a written-down fallback.** A hint
 * that guesses «1920» for one frame teaches the number to whoever is reading,
 * and it stops being true the day an operator edits the row.
 */
function artworkPolicyParts(policy: ArtworkPolicy | undefined): [string, string, string, string] {
  return [
    (policy?.accepted_formats ?? []).join(' · '),
    formatNumber(bytesToMegabytes(policy?.max_size_bytes)),
    // **A pixel dimension is a digit string, not a quantity.** `formatNumber`
    // groups it — «١٬٩٢٠×١٬٠٨٠» — and a grouped figure beside a `×` reads as an
    // amount rather than a measurement. The cap above it IS a quantity, and
    // keeps its grouping.
    toArabicDigits(String(policy?.min_width ?? 0)),
    toArabicDigits(String(policy?.min_height ?? 0)),
  ];
}

/** The cap is published in megabytes and held in bytes; the label wants it back. */
function bytesToMegabytes(bytes: number | null | undefined): number {
  return typeof bytes === 'number' ? Math.round(bytes / 1_048_576) : 0;
}

/**
 * Everything that is not prose — four cards, in the pack's order: النشر ·
 * الوسوم والكاتب · الصور · عناوين ذات صلة.
 *
 * Cards rather than four open sections: a card bounds its group with a surface
 * and a border, which is what lets the rail be scanned in one look instead of
 * read as a long column of fields with nothing between them.
 *
 * **In the document the rail comes after the canvas, and only the grid puts it
 * beside one.** That order is the reading order and the keyboard order at once:
 * someone who opens the page to write reaches the title on the first `Tab`,
 * not after a dozen settings.
 *
 * **The state is shown and never picked.** The lifecycle is transitions with
 * owners, not a field a writer types — a `<select>` carrying «منشور» would
 * offer the writer a route the two-person rule forbids them.
 */

export function PostEditorRail({
  state,
  authorName,
  tags,
  tagIds,
  onTagsChange,
  relatedCandidates,
  onRelatedSearch,
  relatedIds,
  onRelatedChange,
  relatedLabels,
  stateHint,
  publishNotice,
  thumbnailUrl = null,
  heroArtworkUrl = null,
  onArtworkFile,
  artworkBusy = false,
  artworkPolicy,
  artworkError = null,
  disabled = false,
}: {
  state: EditorialState;
  /** The signed-in operator's display name — who this post WILL be authored by. */
  authorName: string;
  tags: readonly PostTag[];
  tagIds: readonly string[];
  onTagsChange: (ids: string[]) => void;
  relatedCandidates: readonly RelatedTitle[];
  onRelatedSearch: (term: string) => void;
  relatedIds: readonly string[];
  onRelatedChange: (ids: string[]) => void;
  /** Titles for ids the loaded page does not carry — never dropped silently. */
  relatedLabels: Readonly<Record<string, string>>;
  stateHint: string;
  thumbnailUrl?: string | null;
  heroArtworkUrl?: string | null;
  /** Absent on the create route — there is no subject to register artwork against yet. */
  onArtworkFile?: ((file: File, slot: ArtworkSlot) => void) | undefined;
  artworkBusy?: boolean;
  /** The published upload contract — every number the two hints print. */
  artworkPolicy?: ArtworkPolicy | undefined;
  /** A refusal, and WHICH of the two controls it answers. */
  artworkError?: ArtworkFailure | null;
  /** The «النشر بيد مراجِع» notice — the create route draws it, the edit route does not. */
  publishNotice?: { title: string; body: string };
  disabled?: boolean;
}) {
  return (
    <aside aria-label={editorCopy.railLabel} className="grid gap-4">
      <p className="mb-0 max-w-none text-xs text-ink-muted">{editorCopy.requiredNote}</p>

      <RailCard heading={editorCopy.publish.heading}>
        <div className="grid gap-2">
          <span className="text-sm font-medium">{editorCopy.publish.stateLabel}</span>
          <span>
            <PostStateBadge state={state} />
          </span>
          <p className="mb-0 max-w-none text-xs text-ink-muted">{stateHint}</p>
        </div>

        {publishNotice ? (
          <Alert kind="info" title={publishNotice.title} className="mt-4">
            {publishNotice.body}
          </Alert>
        ) : null}
      </RailCard>

      <RailCard heading={editorCopy.taxonomy.heading}>
        {/*
          THE AUTHOR IS STATED, NOT CHOSEN.

          The owning service takes the author off the token —
          `author_user_id=principal.require_user_id()` — and its request model is
          `extra="forbid"`, so an `authorId` on the wire was a 422 rather than a
          field it honoured. The control that used to sit here was therefore a
          `<select>` over a set the operator could not actually pick from: it
          listed every account the register returned, showed them by handle
          (`u88n6b4yp42`), and changed nothing whichever one was chosen.

          It is also the right shape on its own terms. §13's two-person review
          turns on the author being the caller — a console that could name
          someone else as the writer would be choosing whose post a reviewer is
          barred from approving, and whose name a rejection is addressed to.
        */}
        <div className="mb-4 grid gap-2">
          <span className="text-sm font-medium">{editorCopy.taxonomy.authorLabel}</span>
          <p className="mb-0 text-body text-ink">{authorName}</p>
          <p className="mb-0 max-w-none text-xs text-ink-muted">{editorCopy.taxonomy.authorHint}</p>
        </div>

        <div className="grid gap-2">
          <span className="text-sm font-medium">{editorCopy.taxonomy.tagsLabel}</span>
          {/*
            Picked from the existing vocabulary, never typed. A free field would
            produce two spellings of one tag and split every listing that uses
            it, silently.
          */}
          <VocabularyPicker
            mode="multi"
            disabled={disabled}
            options={tags.map((tag) => ({ id: tag.id, label: tag.name }))}
            value={tagIds}
            onChange={onTagsChange}
            copy={{
              searchLabel: editorCopy.taxonomy.tagsSearch,
              searchPlaceholder: editorCopy.taxonomy.tagsPlaceholder,
              resultCount: editorCopy.taxonomy.tagsCount,
              noResults: editorCopy.taxonomy.tagsNoResults,
              remove: editorCopy.taxonomy.tagsRemove,
            }}
          />
          <p className="mb-0 max-w-none text-xs text-ink-muted">
            {editorCopy.taxonomy.tagsHint}{' '}
            <Link to="/taxonomy/tags" className="text-accent-text">
              {editorCopy.taxonomy.tagsLink}
            </Link>
          </p>
        </div>
      </RailCard>

      <RailCard heading={editorCopy.art.heading}>
        <p className="mb-4 max-w-none text-xs text-ink-muted">{editorCopy.art.lead}</p>
        {/*
          **The thumbnail is not marked required, and the control is live only
          once the post exists.**

          It used to render a required asterisk over a permanently disabled
          button beside «رفع الصور يفتح مع سطح الملفّات» — a field an operator was
          told they must fill and had no way to fill. The upload routes were
          published all along (`POST /artworks`, the presigned PUT, then
          `:completeUpload`), and `EDITORIAL_POST` is one of the subject types
          they accept; what was missing was a caller.

          Artwork is registered against a subject id, so a post that has not been
          saved yet has nothing to attach it to. On the create route the control
          says so and the asterisk is gone; on the edit route it uploads.
        */}
        <ArtField
          label={editorCopy.art.thumbLabel}
          hint={editorCopy.art.thumbHint(...artworkPolicyParts(artworkPolicy))}
          ratio="aspect-[16/9]"
          preview={artworkUrl(thumbnailUrl)}
          disabled={disabled}
          onFile={onArtworkFile ? (file) => onArtworkFile(file, 'thumbnail') : undefined}
          accept={artworkPolicy?.accepted_formats.join(',')}
          busy={artworkBusy}
          error={artworkError?.slot === 'thumbnail' ? artworkError.message : null}
          pendingHint={onArtworkFile ? undefined : editorCopy.art.uploadAfterSave}
        />
        <ArtField
          label={editorCopy.art.heroLabel}
          hint={editorCopy.art.heroHint(...artworkPolicyParts(artworkPolicy))}
          ratio="aspect-[12/5]"
          preview={artworkUrl(heroArtworkUrl)}
          disabled={disabled}
          onFile={onArtworkFile ? (file) => onArtworkFile(file, 'hero') : undefined}
          accept={artworkPolicy?.accepted_formats.join(',')}
          busy={artworkBusy}
          error={artworkError?.slot === 'hero' ? artworkError.message : null}
          pendingHint={onArtworkFile ? undefined : editorCopy.art.uploadAfterSave}
        />
      </RailCard>

      <RailCard heading={editorCopy.related.heading}>
        <p className="mb-4 max-w-none text-xs text-ink-muted">{editorCopy.related.lead}</p>

        {relatedIds.length === 0 ? (
          // A list inside a rail card whose action sits immediately below it —
          // a line, not an empty state.
          <p className="mb-4 max-w-none text-xs text-ink-muted">{editorCopy.related.empty}</p>
        ) : (
          <ul aria-label={editorCopy.related.listLabel} className="m-0 mb-4 grid list-none gap-2 p-0">
            {relatedIds.map((id) => {
              const title = relatedCandidates.find((candidate) => candidate.id === id);
              const label = title?.title ?? relatedLabels[id] ?? id;

              return (
                <li
                  key={id}
                  className="flex items-center gap-3 rounded-sm border border-border bg-surface p-2"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm">{label}</span>
                    {title ? (
                      <span className="block text-xs text-ink-muted">
                        {title.content_type}
                        {typeof title.production_year === 'number' ? (
                          <>
                            {' · '}
                            <bdi>{formatNumber(title.production_year)}</bdi>
                          </>
                        ) : null}
                      </span>
                    ) : null}
                  </span>
                  <button
                    type="button"
                    disabled={disabled}
                    aria-label={editorCopy.related.remove(label)}
                    onClick={() => onRelatedChange(relatedIds.filter((current) => current !== id))}
                    className="flex size-8 flex-none items-center justify-center rounded-sm text-ink-muted hover:bg-hover-bg hover:text-ink disabled:opacity-45"
                  >
                    ×
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {/*
          The pack draws a search-and-attach control over the catalogue, and the
          search runs ON THE SERVICE — `GET /admin/contentItems?q=` — rather than
          over whatever page this component happens to be holding.

          It used to filter one unparameterised page of 20 rows out of 357 and
          say «لا عنوان بهذا الاسم» when the term missed, which an operator reads
          as «no such title» rather than as a ceiling they cannot see.
        */}
        <VocabularyPicker
          mode="multi"
          disabled={disabled}
          onSearch={onRelatedSearch}
          options={relatedCandidates.map((title) => ({ id: title.id, label: title.title }))}
          value={relatedIds}
          onChange={onRelatedChange}
          copy={{
            searchLabel: editorCopy.related.search,
            searchPlaceholder: editorCopy.related.placeholder,
            resultCount: editorCopy.related.count,
            noResults: editorCopy.related.noResults,
            remove: editorCopy.related.remove,
          }}
        />
        <p className="mb-0 mt-2 max-w-none text-xs text-ink-muted">{editorCopy.related.hint}</p>
      </RailCard>
    </aside>
  );
}

function RailCard({ heading, children }: { heading: string; children: ReactNode }) {
  const headingId = useId();

  return (
    <section
      aria-labelledby={headingId}
      className="overflow-hidden rounded-md border border-border bg-surface"
    >
      <div className="p-4">
        <h2 id={headingId} className="mb-2 font-display text-h4">
          {heading}
        </h2>
        {children}
      </div>
    </section>
  );
}

/**
 * One artwork slot — the preview, its control, and its dimension guidance.
 *
 * The control is **disabled with its reason stated beside it** rather than
 * absent: the presigned upload contract belongs to the assets surface and has
 * not landed, and hiding the field would say this post has no artwork slot
 * when it has one nobody can fill yet.
 */
function ArtField({
  label,
  hint,
  ratio,
  preview,
  onFile,
  accept,
  error = null,
  busy = false,
  pendingHint,
  disabled,
}: {
  label: string;
  hint: string;
  ratio: string;
  /** The stored artwork, drawn instead of the placeholder once one exists. */
  preview?: string | null;
  /** Absent while the subject does not exist yet — see the note at the call site. */
  onFile?: ((file: File) => void) | undefined;
  /** The accepted formats, as the service publishes them — never a literal. */
  accept?: string | undefined;
  /** This control's own refusal, drawn under its own hint. */
  error?: string | null;
  busy?: boolean;
  /** Says why the control cannot act, whenever it cannot. */
  pendingHint?: string | undefined;
  disabled: boolean;
}) {
  const inputId = useId();
  const blocked = disabled || busy || !onFile;

  return (
    <div className="mb-4">
      <span className="mb-2 block text-sm font-medium">{label}</span>

      {preview ? (
        <img
          src={preview}
          alt=""
          className={`my-2 block w-full rounded-sm border border-border object-cover ${ratio}`}
        />
      ) : (
        <span
          aria-hidden="true"
          className={`my-2 block w-full rounded-sm border border-border bg-[linear-gradient(145deg,color-mix(in_srgb,var(--brand-noir)_72%,var(--brand-bone)_28%),color-mix(in_srgb,var(--brand-noir)_90%,var(--brand-bone)_10%)_55%,color-mix(in_srgb,var(--brand-noir)_80%,var(--brand-indigo)_20%))] ${ratio}`}
        />
      )}

      <div className="mt-2 flex flex-wrap gap-2">
        {/*
          A real file input, labelled as a button. The input itself is visually
          hidden rather than `display:none` so it keeps its place in the tab order
          and the label stays its accessible name.
        */}
        <input
          id={inputId}
          type="file"
          accept={accept ?? 'image/*'}
          disabled={blocked}
          className="sr-only"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) onFile?.(file);
            // Clear the control so choosing the SAME file twice fires again — an
            // operator who re-picks after a failed upload expects a retry.
            event.target.value = '';
          }}
        />
        <label
          htmlFor={inputId}
          aria-disabled={blocked}
          className={`inline-flex min-h-6 items-center rounded-sm border border-border-strong bg-surface-raised px-3 text-xs font-semibold text-ink ${
            blocked ? 'cursor-not-allowed opacity-45' : 'cursor-pointer hover:bg-surface-overlay'
          }`}
        >
          {busy ? editorCopy.art.uploading : editorCopy.art.upload}
        </label>
      </div>

      <p className="mb-0 mt-2 max-w-none text-xs text-ink-muted">{pendingHint ?? hint}</p>

      {/*
        **The refusal belongs to THIS control, under THIS control.**

        It first travelled to `PostEditor`'s shared `banner` slot, below the
        entire two-column grid — the operator pressed the button, the file dialog
        closed, nothing visibly changed, and the explanation was off the bottom
        of the window. Moving it into the الصور card was not enough on its own:
        measured on develop, the card-level alert sat 385 px BELOW the button
        that had been pressed, which on a 900 px viewport is still off-screen.
        Under the field's own hint it is adjacent to the control at any height.

        `role="alert"` because a file input's own state does not change on a
        refusal, so nothing else announces it.
      */}
      {error ? (
        <p
          role="alert"
          className="mb-0 mt-2 rounded-sm border border-s-4 border-border border-s-danger bg-danger-soft p-2 text-xs text-ink"
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}
